import React, { useState } from 'react';
import Icon from './icons';
import MasterPanel from './MasterPanel';
import GenDocRow from './GenDocRow';
import ChangeDoc from './ChangeDoc';
import RegDateBar from './RegDateBar';
import DocFieldModal from './DocFieldModal';
import { fileUrl } from './api';
import { MasterCollapse, MasterChip, getS3, getUp, getUpKey, s3UploadHandler, OkFile, UpBtn, RefFile, Wait, S3Row, FormDl, BulkSaveBtn, dispName } from './stepcommon';
import { collectGenFiles, openS3, downloadCollectFile, downloadUploadFile, fetchCollectBlob } from './docgen';
import { getChangeVers, changeFileName } from './changedoc';
import type { Project, Master, StoreKey } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  masterOpen: boolean;
  onToggleMaster: () => void;
  onPv: (k: string, idx?: number) => void;
  onVer: (k: string) => void;
  onFilePv: (name: string, title?: string) => void;
}

export default function Step7({ p, onPatch, toast, masterOpen, onToggleMaster, onPv, onVer, onFilePv }: Props) {
  const m = p.master;
  const [changeFormOpen, setChangeFormOpen] = useState(false);
  const vics = getS3(p).vics;
  // 고유번호 — 숫자 10자리가 되면 123-82-00456 모양으로 · 5단계에 올린 고유번호증을 보며 적는 창 (2026-11-01 지도사 「윗 단계에서 넣은 고유번호증 보면서 입력」)
  const [uniqOpen, setUniqOpen] = useState(false);
  const uniqFn = getUp(p, 's5', 'unique');
  const uniqKey = getUpKey(p, 's5', 'unique');
  const uniqDisp = uniqFn ? dispName(uniqFn, '조합 고유번호증', p.org || '') : '';
  const uniqLoad = !uniqFn ? undefined : uniqKey ? () => fileUrl(uniqKey, uniqDisp, true) : () => fetchCollectBlob(p, uniqFn).then((b) => URL.createObjectURL(b));
  const fmtUniq = (v: string): string => { const d = v.replace(/\D/g, ''); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : v; };
  const setUniq = (v0: string) => {
    const v = fmtUniq(v0);
    if (!p.master) return;
    const mm = JSON.parse(JSON.stringify(p.master)) as Master;
    mm.uniqueNo = v;
    onPatch({ master: mm });
  };
  const gd = (k: string, seal: boolean) => <GenDocRow p={p} k={k} seal={seal} onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />;
  const refup = (store: StoreKey, k: string, title: string, wait: string) => {
    const fn = getUp(p, store, k);
    if (!fn) return <Wait msg={wait} />;
    const disp = dispName(fn, title, p.org || '');
    const key = getUpKey(p, store, k);
    return key
      ? <RefFile name={disp} tag="앞단계 자동 연동" onPv={() => openS3(key, disp, true).catch((e) => toast((e as Error).message))} onDl={() => downloadUploadFile(p, key, disp, toast)} />
      : <RefFile name={disp} tag="앞단계 자동 연동" onPv={() => onFilePv(fn, title)} onDl={() => downloadCollectFile(p, fn, toast)} />;
  };
  const upChange = s3UploadHandler(p, 's7', 'change', onPatch, toast, '결성계획서 대비 변경내역');
  const changeCur = getUp(p, 's7', 'change');
  const changeKey = getUpKey(p, 's7', 'change');
  const changeDisp = changeCur ? dispName(changeCur, '결성계획서 대비 변경내역', p.org || '') : changeCur;
  const upF = (store: StoreKey, k: string, title: string) => {
    const ufn = getUp(p, store, k);
    if (!ufn) return [];
    const nm = dispName(ufn, title, p.org || '');
    const ukey = getUpKey(p, store, k);
    return ukey ? [{ key: ukey, name: nm }] : [{ key: '', name: nm, getBlob: () => fetchCollectBlob(p, ufn) }];
  };
  // 14. 결성계획서 대비 변경내역 — 자동 생성본(최신 버전) + 직접 업로드본을 일괄 다운로드에 포함
  const changeVer = getChangeVers(p).slice(-1)[0];
  const bulkFiles = [
    ...collectGenFiles(p, ['admininfo', 'regform', 'minutes', 'roster', 'jeungpyo', 'lpprivacy', 'rule', 'inkam']),
    ...upF('s6', 'balance', '조합통장 잔고증명서'),
    ...upF('s6', 'capproof', '조합원 출자이행 증빙서류'),
    ...upF('s5', 'unique', '조합 고유번호증'),
    ...upF('s6', 'credit', 'GP 신용정보조회서'),
    ...(changeVer && changeVer.s3doc ? [{ key: changeVer.s3doc, name: changeVer.doc || changeFileName(p, 'docx') }] : []),
    ...(changeVer && changeVer.s3pdf ? [{ key: changeVer.s3pdf, name: changeVer.pdf || changeFileName(p, 'pdf'), signed: changeVer.kind === 'seal' }] : []),
    ...upF('s7', 'change', '결성계획서 대비 변경내역'),
  ];

  return (
    <div className="sact">
      <div className="sact-h" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📋 조합결성 등록</span>
        <BulkSaveBtn files={bulkFiles} toast={toast} pid={p.id} />
      </div>
      <MasterCollapse open={masterOpen} onToggle={onToggleMaster} chip={<MasterChip m={p.master} />} title="📋 공통정보 (마스터)" sub="열기를 누르면 펼쳐집니다 · 앞 단계 입력 그대로 연동">
        <MasterPanel p={p} onPatch={onPatch} toast={toast} withAnswers={false} regenKeys={['admininfo', 'regform', 'minutes', 'roster', 'jeungpyo', 'lpprivacy']} regenLabel="마스터값으로 7단계 문서 재생성" regenToast="마스터값으로 7단계 문서 재생성됨" />
      </MasterCollapse>
      <div className="uniqbar">
        <label>조합 고유번호 <span>고유번호증 발급 후 입력 · 등록신청서·증표에 자동 반영</span></label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ flex: 1, minWidth: 0 }} value={(m && m.uniqueNo) || ''} onChange={(e) => setUniq(e.target.value)} placeholder="예: 123-82-00456" />
          <button type="button" className="rbtn" style={{ flexShrink: 0, padding: '0 14px' }} onClick={() => setUniqOpen(true)}
            title={uniqFn ? '5단계에 올린 고유번호증을 보며 적기' : '5단계에서 고유번호증을 올리면 여기서 보며 적을 수 있습니다'}>📄 고유번호증 보며</button>
        </div>
      </div>
      {uniqOpen && (
        <DocFieldModal
          title={(p.org || '조합') + ' · 고유번호증'}
          fileName={uniqDisp}
          load={uniqLoad}
          noneMsg="5단계 「조합통장 개설서류 발송」의 1번 고유번호증을 올리면 여기서 보며 적을 수 있습니다"
          fields={[{ key: 'uniqueNo', label: '조합 고유번호 (고유번호증의 「고유번호」)', value: (m && m.uniqueNo) || '', hint: '123-82-00456', onChange: setUniq }]}
          index={0}
          onIndex={() => undefined}
          onClose={() => setUniqOpen(false)}
        />
      )}
      <RegDateBar p={p} onPatch={onPatch} toast={toast} />
      <div className="lbl" style={{ marginTop: 14 }}>등록 서류 (13) · 워드·PDF 자동 생성, 앞단계 자동 연동</div>
      <S3Row no={1} title="VICS ID·PW" note="3단계에서 입력한 값 그대로 표시">
        <div className="s3file">
          <span className="rf">🔑 ID <b>{vics.id || '-'}</b>{'\u00A0\u00A0'}PW <b>{vics.pw || '-'}</b></span>
          <span className="pill p-blue">3단계 입력 자동</span>
        </div>
      </S3Row>
      <S3Row no={2} title="GP 행정정보 공동이용 사전동의서" note="공식 양식 PDF 그대로 · 날짜(결성총회일)·GP 이름·주민번호·휴대폰 자동 입력"><GenDocRow p={p} k="admininfo" seal pdfOnly onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} /></S3Row>
      <S3Row no={3} title="개인투자조합 규약" note="3단계 규약 자동 연동 · 여기서 수정하면 앞단계도 반영">{gd('rule', false)}</S3Row>
      <S3Row no={4} title="개인투자조합 등록 신청서" note="조합명·고유번호·GP·LP수·출자총액·주소·전화·날짜·신청인·존속기간 5년 자동">{gd('regform', true)}</S3Row>
      <S3Row no={5} title="결성총회 의사록" note="빨간 항목(일시·장소·인원·좌수·GP/LP·주민번호·날짜) 자동">{gd('minutes', true)}</S3Row>
      <S3Row no={6} title="조합원 명부" note="앞단계 명부 자동">{gd('roster', true)}</S3Row>
      <S3Row no={7} title="출자증표" note="빨간 항목(출자자·주민번호·주소·출자금·좌수·날짜) 자동"><GenDocRow p={p} k="jeungpyo" seal pdfOnly onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} /></S3Row>
      <S3Row no={8} title="조합인감등록부" link={{ label: '모두싸인 바로가기', href: 'https://modusign.co.kr/' }} note="5단계 인감등록부 자동 연동 · 여기서 수정하면 앞단계도 반영">{gd('inkam', false)}</S3Row>
      <S3Row no={9} title="LP 개인정보 수집·이용·제공 동의서" note="PDF에 LP 주민번호·이름·날짜(결성총회일) 자동">
        <GenDocRow p={p} k="lpprivacy" seal={false} pdfOnly onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />
      </S3Row>
      <S3Row no={10} title="조합통장 잔고증명서">{refup('s6', 'balance', '조합통장 잔고증명서', '6단계에서 업로드하면 자동 연동')}</S3Row>
      <S3Row no={11} title="조합원 출자이행 증빙서류">{refup('s6', 'capproof', '조합원 출자이행 증빙서류', '6단계에서 업로드하면 자동 연동')}</S3Row>
      <S3Row no={12} title="조합 고유번호증">{refup('s5', 'unique', '조합 고유번호증', '5단계에서 업로드하면 자동 연동')}</S3Row>
      <S3Row no={13} title="GP 신용정보조회서">{refup('s6', 'credit', 'GP 신용정보조회서', '6단계에서 업로드하면 자동 연동')}</S3Row>
      <S3Row no={14} title="결성계획서 대비 변경내역" opt action={<button className="rbtn" onClick={() => setChangeFormOpen((o) => !o)} title="LP 변경 입력 (탈퇴자 → 신규자)">✏ 변경입력 (OOO→OOO) {changeFormOpen ? '▲' : '▼'}</button>}>
        <div className="s3note">조합결성 신청 후 LP가 변경된 경우 · 우측 [변경입력]에서 탈퇴자→신규자를 넣으면 자동 생성됩니다</div>
        <ChangeDoc p={p} formOpen={changeFormOpen} onPatch={onPatch} toast={toast} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <FormDl href="/venture/forms/plan_change_history.hwp" name="결성계획서 대비 변경내역(양식).hwp" />
          {changeCur ? <OkFile name={changeDisp} onPv={changeKey ? () => openS3(changeKey, changeDisp, true).catch((e) => toast((e as Error).message)) : undefined} onDl={() => downloadUploadFile(p, changeKey, changeDisp, toast)} onRe={upChange} /> : <UpBtn label="직접 업로드(선택)" onUp={upChange} />}
        </div>
      </S3Row>
      <button className="btn pri sm" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={() => { window.open('https://www.smes.go.kr/venturein/', '_blank', 'noopener'); toast('벤처확인종합관리시스템(VICS)에서 조합결성을 등록하세요'); }}>
        <Icon n="send" style={{ width: 13, height: 13 }} /> VICS에 조합결성 등록
      </button>
    </div>
  );
}
